<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching && !initialized")
    v-skeleton-loader.py-8.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Title and actions bars
    v-row.mt-6(no-gutters align="center")
      v-col
        .text-md.font-semibold {{ $t('visits') }}
      v-col(align="end")
        .d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
          div
            customize-column-menu(:type="getColumnMenuType" :showTooltip="false" iconSize="21")
    v-divider
    //- Data listing
    .data-table(:class="{ 'is-doctor':isDoctor }")
      v-data-table.mt-sm-4.pb-6(:headers="headers" :items="patientHistories" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" disable-sort @click:row="onOpenVisitSummary" loader-height="2")
        //- payment status
        template(v-slot:item.invoice="{ item }")
          v-chip.round-lg(v-if="item.invoice.total_amount === 0" label color="success100")
            v-icon.ma-0.pr-1(color="success" size="8") mdi-circle
            .text-sm.success500--text N/A
          v-chip.round-lg(v-else-if="hasPaid(item.invoice)" label color="success100")
            v-icon.ma-0.pr-1(color="success" size="8") mdi-circle
            .text-sm.success500--text {{ $t('paid') }}
          v-chip.round-lg(v-else label color="danger100")
            v-icon.ma-0.pr-1(color="danger" size="8") mdi-circle
            .text-sm.danger500--text.no-wrap {{ $t('due') }}: {{ getTotalBalance(item.invoice) | displayPrice }}
        //- diagnosis
        template(v-slot:item.formatted_diagnoses="{ item }")
          div.d-flex.align-center(v-if="item.formatted_diagnoses")
            template(v-for="diagnosis in item.formatted_diagnoses")
              y-chip(:label="diagnosis.length < 25 ? diagnosis : `${diagnosis.substr(0, 25)}...`" size="sm" chip-class="pa-1 mr-2" :key="diagnosis" outlined)
            v-tooltip(v-if="item.formatted_diagnoses.length !== item.consultation.diagnoses.length" top color="neutral700")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  y-chip.cursor-help(:label="`+${item.consultation.diagnoses.length - item.formatted_diagnoses.length } more`" size="sm" chip-class="pa-1" outlined)
              div
                div(v-for="(itemDiag, index) in item.consultation.diagnoses" :key="itemDiag") {{ index + 1 }}. {{ itemDiag }}
        //- payment methods
        template(v-slot:item.payment_methods="{ item }")
          div.d-flex.align-center(v-if="item.payment_methods")
            template(v-for="payment in item.payment_methods")
              y-chip(:label="payment.length < 25 ? payment : `${payment.substr(0, 25)}...`" size="sm" chip-class="pa-1 mr-2" :key="payment" outlined)
            v-tooltip(v-if="item.payment_methods.length !== item.transactions.length" top color="neutral700")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  y-chip.cursor-help(:label="`+${item.transactions.length - item.payment_methods.length } more`" size="sm" chip-class="pa-1" outlined)
              div
                div(v-for="(transaction, index) in item.all_payment_methods" :key="transaction") {{ index + 1 }}. {{ transaction }}
        //- actions
        template(v-slot:item.actions="{ item }")
          v-menu(offset-y left rounded="lg")
            template(v-slot:activator="{ on }")
              v-btn(icon small v-on="on")
                v-icon mdi-dots-horizontal
            v-list.py-0
              v-list-item(@click="onOpenVisitSummary(item)")
                v-list-item-title.text-sm.basic800--text {{ $t('view_visit_summary') }}
    div.d-flex.align-center.mb-4(v-if="totalCount > pageSize")
      v-spacer
      div
        yc-pagination-custom(v-model="page" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
  .dialogs
    visit-summary-dialog(v-if="showVisitSummaryDialog" :open.sync="showVisitSummaryDialog" :visit="selectedItem" :patientId="patientId" @closed="selectedItem = null" @updated="onUpdated")
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import VisitSummaryDialog from '@/components/clinic/dialogs/VisitSummaryDialog.vue'

export default {
  components: {
    CustomizeColumnMenu, VisitSummaryDialog
  },
  data() {
    return {
      isFetching: true,
      initialized: false,
      showSearchField: false,
      showVisitSummaryDialog: false,
      selectedItem: null,
      searchText: null,
      patientHistories: null,
      totalCount: 0,
      pageSize: 20,
      page: 1,
    }
  },
  watch: {
    params() {
      this.fetchPatientHistory()
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
      doctorVisitFields: 'clinic/getDoctorVisitFields',
      nonDoctorVisitFields: 'clinic/getNonDoctorVisitFields'
    }),
    headers() {
      const fields = this.isDoctor ? this.doctorVisitFields.filter(item => item.enable) : this.nonDoctorVisitFields.filter(item => item.enable)
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
      })
      const actions = {
        text: '',
        value: 'actions',
        enable: true,
        sortable: false
      }
      // Return header values
      if (this.isDoctor) return [...fields, actions]

      return [...fields]
    },
    patientId () {
      if (this.$route.params.id) return parseInt(this.$route.params.id)

      return null
    },
    getColumnMenuType () {
      if (this.isDoctor) return 'doctor_visit'

      return 'non_doctor_visit'
    }
  },
  mounted () {
    this.fetchPatientHistory()
  },
  methods: {
    ...mapActions(['showMessage']),
    pageChanged () {
      this.fetchPatientHistory()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchPatientHistory()
    }, 750),
    async fetchPatientHistory() {
      try {
        this.isFetching = true
        let params = {
          page: this.page,
          page_size: this.pageSize,
          search: this.searchText,
          fields: [
            'lead_id',
            'lead_pid',
            'invoice',
            'record_date',
            'provider',
            'consultation',
            'transactions'
          ].join()
        }
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}/histories`, { params })
        this.patientHistories = data.results.map(result => this.sanitizeData(result))
        this.totalCount = data.count
        this.isFetching = false
        this.initialized = true
        this.checkQuery()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    sanitizeData (data) {
      // Record date
      const formatted_date = data.record_date ? this.displayDate(data.record_date) : null
      // Doctor
      const doctor_name = data.provider ? data.provider.name : null
      // Diagnoses
      const formatted_diagnoses = data?.consultation?.diagnoses ? this.filterDiagnoses(data.consultation.diagnoses) : null
      // Payment methods
      const all_payment_methods = data?.transactions ? this.formatPaymentMethods(data.transactions) : []
      const payment_methods = data?.transactions ? this.filterPaymentMethods(all_payment_methods) : null

      return {
        ...data,
        formatted_date,
        doctor_name,
        formatted_diagnoses,
        payment_methods,
        all_payment_methods
      }
    },
    displayDate (value) {
      if (!value) return null

      const date = DateTime.fromISO(value)
      const formatDate = date.toFormat('d MMM yyyy')
      const formatTime = date.toFormat('HH:mm a').toLowerCase()

      return `${formatDate}, ${formatTime}`
    },
    filterDiagnoses (array) {
      if (array.length > 1) {
        let newArray = []
        let count = 0
        array.forEach((o, idx) => {
          count += o.length
          if (idx === 0 || count <= 25) {
            newArray.push(o)
          }
        })
        return newArray
      }
      return array
    },
    formatPaymentMethods (array) {
      return array.map((item) => {
        let resp = item.payment_method
        if (item.channel === 'panel') {
          resp = 'Panel: ' + resp
        }
        return resp
      })
    },
    filterPaymentMethods (array) {
      if (array.length > 1) {
        let newArray = []
        let count = 0
        array.forEach((o, idx) => {
          count += o.length
          if (idx === 0 || count <= 25) {
            newArray.push(o)
          }
        })
        return newArray
      }
      return array
    },
    hasPaid (invoice) {
      if (!invoice) return false

      return invoice.paid
    },
    getTotalBalance (invoice) {
      if (!invoice) return 0

      return invoice.total_amount - invoice.total_paid
    },
    onOpenVisitSummary (item) {
      if (!this.isDoctor) return
      
      this.selectedItem = item
      this.showVisitSummaryDialog = true
    },
    checkQuery () {
      if (this.$route.query?.leadPid) {
        const found = this.patientHistories.find((history) => history.lead_pid === this.$route.query.leadPid)
        if (found) {
          this.onOpenVisitSummary(found)
          // Reset query
          this.$router.replace({ query: { tab:'visits' } })
        }
      }
    },
    onUpdated () {
      this.$emit('updated')
      this.fetchPatientHistory()
    }
  },
}
</script>

<style lang="scss" scoped>
.is-doctor ::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
</style>